import { ConfiguredJob } from "common/state/rtcJobsApi";
import { FeatureCollection, LineString, Polygon } from "geojson";
import { Layer, Source } from "react-map-gl";
import { lineString } from "@turf/helpers";
import { makeTaskRenderer, TaskWith } from "../TaskLayers";
import {
  mapboxGetColorForTaskState,
  mapboxGetOpacityForTaskState,
} from "common/components/map/layers/mapboxHelpers.task";
import { TASK_BASE_PROPS, TaskFeatureProperties } from "common/utils/rtcJobs";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import buffer from "@turf/buffer";
import React, { useMemo } from "react";

// Row width fallback when it can't be calculated from the planting path
export const DEFAULT_ROW_WIDTH = 6.7056;

function taskBoxesForWorkRowTasks(
  configuredJob: ConfiguredJob,
  tasks: TaskWith<"workRow">[]
): {
  progressPaths: FeatureCollection<Polygon, TaskFeatureProperties>;
  outlines: FeatureCollection<Polygon, TaskFeatureProperties>;
} {
  const rowWidthMeters =
    configuredJob.inSeason?.rowSpacingM || DEFAULT_ROW_WIDTH;

  const progressPaths: FeatureCollection<Polygon, TaskFeatureProperties> = {
    type: "FeatureCollection",
    features: tasks.flatMap((t) => {
      const points = t.progressPath?.points ?? t.workRow.path?.points;
      if (!points || points.length < 2) {
        return [];
      }
      const properties = {
        ...TASK_BASE_PROPS,
        task: t,
      };

      const bufferedLine = buffer<LineString>(
        lineString(points.map((pt) => [pt.lng, pt.lat])),
        rowWidthMeters / 4,
        { units: "meters" }
      );

      return [{ ...bufferedLine, properties }];
    }),
  };
  const outlines: FeatureCollection<Polygon, TaskFeatureProperties> = {
    type: "FeatureCollection",
    features: tasks.flatMap((t) => {
      const pathPoints = t.workRow.path?.points;
      if (!pathPoints || pathPoints.length < 2) {
        return [];
      }
      const properties = {
        ...TASK_BASE_PROPS,
        task: t,
      };
      const outlineBufferedLine = buffer<LineString>(
        lineString(pathPoints.map((pt) => [pt.lng, pt.lat])),
        rowWidthMeters / 4,
        { units: "meters" }
      );

      return [{ ...outlineBufferedLine, properties }];
    }),
  };

  return { progressPaths, outlines };
}

enum LayerIds {
  FILL = "wrt-fill",
  OUTLINE = "wrt-outline",
}

export const renderWorkRowTask = makeTaskRenderer({
  detailsField: "workRow",
  Render: ({ configuredJob, tasks, extraLayerProps, beforeId }) => {
    const { progressPaths, outlines } = useMemo(() => {
      return taskBoxesForWorkRowTasks(configuredJob, tasks);
    }, [configuredJob, tasks]);
    return (
      <>
        <Source data={progressPaths} type="geojson">
          <Layer
            id={LayerIds.FILL}
            beforeId={LayerIds.OUTLINE}
            type="fill"
            paint={{
              "fill-color": ["coalesce", mapboxGetColorForTaskState, "#FFF"],
              "fill-opacity": mapboxGetOpacityForTaskState,
            }}
            {...extraLayerProps}
          />
        </Source>
        <Source data={outlines} type="geojson">
          <Layer
            id={LayerIds.OUTLINE}
            beforeId={beforeId}
            type="line"
            paint={{
              "line-color": theme.colors.black,
            }}
            {...extraLayerProps}
          />
        </Source>
      </>
    );
  },
  interactiveLayerIds: [LayerIds.FILL],
  imagesToLoad: [],
});
